import { useEffect, useState } from 'react'
import { apiUrl } from '../lib/api'

/**
 * Polední menu — čte veřejný endpoint /api/sobacov/menu/week (Po–Pá).
 * Když pro týden nejsou data, ukáže klidnou hlášku (sekce nikdy nespadne).
 */

type MenuItem = { type: string; position: number | null; title: string; allergens: string | null; price_czk?: number | null }
type MenuDay = { day: string; photo_path: string | null; notes: string | null; items: MenuItem[] }

const CZ_DAY = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota']

function fmtDay(iso: string) {
  const d = new Date(iso + 'T12:00:00')
  return { name: CZ_DAY[d.getDay()], date: `${d.getDate()}. ${d.getMonth() + 1}.` }
}

export function Menu() {
  const [days, setDays] = useState<MenuDay[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    fetch(apiUrl('/api/sobacov/menu/week'))
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => setDays(j?.days?.filter((d: MenuDay) => d.items.length > 0) || []))
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [])

  const todayIso = new Date().toISOString().slice(0, 10)

  return (
    <section id="menu" className="py-20 md:py-28 bg-paper">
      <div className="container-x">
        <div className="max-w-3xl">
          <p className="h-eyebrow">Restaurace</p>
          <h2 className="h-display text-3xl md:text-[44px] mt-4">Polední <span className="h-accent">menu</span></h2>
        </div>

        {loaded && days.length === 0 && (
          <div className="mt-10 card p-6 text-ink-700 text-[15px]">
            Menu na tento týden právě připravujeme — aktuální nabídku vám rádi řekneme
            na&nbsp;<a href="tel:+420775140705" className="font-semibold text-ink-900 hover:text-brand-600">775&nbsp;140&nbsp;705</a>.
          </div>
        )}

        {days.length > 0 && (
          <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3" id="menu-tyden">
            {days.map((d) => {
              const f = fmtDay(d.day)
              const isToday = d.day === todayIso
              const soups = d.items.filter((i) => i.type === 'soup')
              const mains = d.items.filter((i) => i.type !== 'soup')
              return (
                <article key={d.day} className={`card p-5 ${isToday ? 'ring-2 ring-brand-500 border-transparent' : ''}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-bold text-ink-900 text-lg tracking-tight">{f.name}</h3>
                    <div className="flex items-center gap-2">
                      {isToday && (
                        <span className="text-[11px] font-semibold text-brand-700 bg-brand-50 border border-brand-100 rounded-md px-2 py-0.5">
                          Dnes
                        </span>
                      )}
                      <span className="text-[13px] text-ink-700/70 font-medium">{f.date}</span>
                    </div>
                  </div>
                  {soups.length > 0 && (
                    <div className="mt-3 text-[13px] text-ink-700">
                      <span className="uppercase tracking-[0.14em] text-[10px] font-semibold text-ink-700/60">Polévky</span>
                      <ul className="mt-1 space-y-0.5">
                        {soups.map((s, i) => (
                          <li key={i} className="flex gap-2.5">
                            <span className="leading-snug">{s.title}</span>
                            {s.price_czk ? <span className="ml-auto shrink-0 font-semibold tabular-nums text-ink-900">{s.price_czk} Kč</span> : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {mains.length > 0 && (
                    <div className="mt-3 text-[14px] text-ink-800">
                      <span className="uppercase tracking-[0.14em] text-[10px] font-semibold text-ink-700/60">Hlavní jídla</span>
                      <ol className="mt-1 space-y-1.5">
                        {mains.map((m, i) => (
                          <li key={i} className="flex gap-2.5">
                            <span className="text-ink-700/50 font-semibold tabular-nums shrink-0">{m.position ?? i + 1}.</span>
                            <span className="leading-snug">{m.title}</span>
                            {m.price_czk ? <span className="ml-auto shrink-0 font-semibold tabular-nums text-ink-900">{m.price_czk} Kč</span> : null}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </article>
              )
            })}
          </div>
        )}

        {/* Restaurace v provozu (Robin 5. 11. 2026, místo „Připravujeme / objednávky telefonicky") */}
        <div className="mt-8 bg-forest-700 text-white rounded-2xl px-6 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <svg viewBox="0 0 24 24" className="w-6 h-6 mt-0.5 shrink-0 text-white/85" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 3v7a3 3 0 0 0 6 0V3M10 3v18M17 3c-1.5 2-2 4-2 7h2v11" />
            </svg>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/60">Restaurace je v provozu</div>
              <div className="mt-1 text-[16px] text-white/95 font-semibold">Těšíme se na vaši návštěvu!</div>
            </div>
          </div>
          <a href="#kontakt" className="border border-white/45 hover:bg-white hover:text-ink-900 text-white px-4 py-2.5 font-semibold text-[14px] rounded-[10px] transition-colors">
            Kudy k nám →
          </a>
        </div>
      </div>
    </section>
  )
}
